import { isSuccess, isUnauthorized } from '@yimoka/shared';
import { action, define, observable } from '@yimoka/store';

import { authAccountEntity } from '@/entities/auth-account';
import type { IAuthAccountStaff } from '@/entities/auth-account.d';
import {
  clearAuthSession,
  getUserToken,
  setTenantStaffs,
  setUser,
  setUserToken,
} from '@/session/auth-session';
import { normalizeInternalRedirect } from '@/utils/auth-redirect';

import { authAccountBiz } from '.';

type Navigate = (to: string, options?: { replace?: boolean }) => unknown;
type TenantJoinStatus = 'checking' | 'error' | 'invalid' | 'ready';

/** TenantJoinBiz 管理 Web 邀请链接的用户校验、员工绑定和目标租户登录状态机。 */
class TenantJoinBiz {
  status: TenantJoinStatus = 'checking';
  tenantID = '';
  tenantName = '';
  token = '';
  currentPath = '';
  submitError = false;
  submitting = false;
  private verifySucceeded = false;

  /** 初始化邀请流程所需的响应式状态。 */
  constructor() {
    define(this, {
      status: observable,
      tenantID: observable,
      tenantName: observable,
      token: observable,
      currentPath: observable,
      submitError: observable,
      submitting: observable,
      mount: action,
      submit: action,
    });
  }

  /** userInfoStore 主动校验平台用户身份，邀请手机号只从该响应读取。 */
  get userInfoStore() {
    return authAccountEntity.userInfoStore;
  }

  /** inviteVerifyStore 保存一次邀请验证操作的参数、loading 和响应。 */
  get inviteVerifyStore() {
    return authAccountEntity.inviteVerifyStore;
  }

  /** user 返回服务端主动校验后的当前平台用户。 */
  get user() {
    return this.userInfoStore.response?.data?.user;
  }

  /** displayTenantName 在旧链接缺少名称时回退展示租户 ID。 */
  get displayTenantName() {
    return this.tenantName || this.tenantID;
  }

  /** canSubmit 仅在参数、用户手机号和请求状态均满足时允许确认邀请。 */
  get canSubmit() {
    return this.status === 'ready'
      && Boolean(this.token && this.tenantID && this.user?.phone && this.user?.phonePrefix)
      && !this.inviteVerifyStore.loading
      && !this.submitting;
  }

  /**
   * mount 校验邀请参数和平台用户 token；未登录或 token 失效时携带完整邀请地址返回登录页。
   */
  mount = async (
    navigate: Navigate,
    currentPath: string,
    search: string,
  ) => {
    this.status = 'checking';
    this.submitError = false;
    this.verifySucceeded = false;
    this.currentPath = normalizeInternalRedirect(currentPath) ?? '/tenant/join';

    const params = new URLSearchParams(search);
    this.token = params.get('token')?.trim() ?? '';
    // 旧站使用 snake_case，新站与部分回跳链路可能使用 camelCase；两种格式都要接住。
    this.tenantID = (params.get('tenant_id') ?? params.get('tenantID'))?.trim() ?? '';
    this.tenantName = (params.get('tenant_name') ?? params.get('tenantName'))?.trim() ?? '';
    if (!this.token || !this.tenantID) {
      this.status = 'invalid';
      return;
    }

    const userStatus = await this.loadCurrentUser(navigate);
    if (userStatus) this.status = userStatus;
  };

  /** submit 防止重复绑定；绑定成功后刷新员工关系并自动进入邀请指定租户。 */
  submit = async (navigate: Navigate) => {
    if (this.submitting || (!this.canSubmit && !this.verifySucceeded)) return;
    this.submitting = true;
    this.submitError = false;

    try {
      if (!this.verifySucceeded) {
        const { user } = this;
        if (!user?.phone || !user.phonePrefix) {
          this.status = 'invalid';
          return;
        }

        this.inviteVerifyStore.setValues({
          phone: user.phone,
          phonePrefix: user.phonePrefix,
          tenantID: this.tenantID,
          token: this.token,
        });
        const verifyRes = await this.inviteVerifyStore.fetch();
        if (!isSuccess(verifyRes) || verifyRes.data !== true) {
          this.submitError = true;
          return;
        }
        this.verifySucceeded = true;
      }

      const staffsRes = await authAccountEntity.tenantStaffsStore.fetch();
      if (!isSuccess(staffsRes)) {
        navigate('/tenant-selector');
        return;
      }
      const staffs = Array.isArray(staffsRes.data) ? staffsRes.data : [];
      setTenantStaffs(staffs);
      const targetStaff = staffs.find(staff => this.isTargetStaff(staff));
      if (!targetStaff) {
        navigate('/tenant-selector');
        return;
      }

      // 登录页仍保留邀请 redirect，绑定完成后必须显式改为工作台，避免再次跳回已消费的邀请链接。
      const loggedIn = await authAccountBiz.handleTenantLogin(
        targetStaff,
        navigate,
        `/t/${encodeURIComponent(this.tenantID)}/workspace`,
      );
      if (!loggedIn) navigate('/tenant-selector');
    } finally {
      // 单飞范围覆盖绑定、刷新关系和租户登录，避免验证成功后的窗口再次触发后半程。
      this.submitting = false;
    }
  };

  /** loadCurrentUser 主动校验平台用户并返回邀请页状态；未登录时保留完整邀请地址跳转。 */
  private loadCurrentUser = async (navigate: Navigate): Promise<TenantJoinStatus | undefined> => {
    if (!getUserToken()) {
      navigate(`/login?redirect=${encodeURIComponent(this.currentPath)}`, { replace: true });
      return undefined;
    }

    const userRes = await this.userInfoStore.fetch();
    if (isUnauthorized(userRes)) {
      clearAuthSession();
      navigate(`/login?redirect=${encodeURIComponent(this.currentPath)}`, { replace: true });
      return undefined;
    }
    if (!isSuccess(userRes)) return 'error';

    if (userRes.data?.token) setUserToken(userRes.data.token);
    if (userRes.data?.user) setUser(userRes.data.user);
    if (!userRes.data?.user?.phone || !userRes.data.user.phonePrefix) {
      return 'invalid';
    }
    return 'ready';
  };

  /** isTargetStaff 只选择邀请租户内仍启用的员工身份，避免绑定后进入停用租户。 */
  private isTargetStaff = (staff: IAuthAccountStaff) => (
    staff.switch !== false
    && staff.tenant?.switch !== false
    && (String(staff.tenantID) === this.tenantID || staff.tenant?.id === this.tenantID)
  );
}

export const tenantJoinBiz = new TenantJoinBiz();
